import type jsPDF from "jspdf";
import type { FacadeReportPhoto } from "./facade-report";

type ReportContext = {
  pdf: jsPDF;
  margin: number;
  width: number;
  pageHeight: number;
  startY: number;
  newPage: () => number;
  compressImage: (src: string) => Promise<string | undefined>;
  imageDimensions: (src: string) => Promise<{ width: number; height: number } | null>;
};

/** Described photos first; photos without descriptions form a separate appendix. */
export async function renderFacadeReport(
  context: ReportContext,
  feedback: string,
  photos: FacadeReportPhoto[],
): Promise<void> {
  const { pdf, margin, width, pageHeight } = context;
  let y = context.startY;
  const ensureSpace = (height: number) => {
    if (y + height > pageHeight - 34) y = context.newPage();
  };
  const paragraph = (text: string, bold = false, size = 9) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(40, 40, 40);
    for (const line of pdf.splitTextToSize(text, width) as string[]) {
      ensureSpace(5);
      pdf.text(line, margin, y);
      y += 4.8;
    }
    y += 3;
  };
  ensureSpace(18);
  paragraph("Schadensdokumentation – ohne Kalkulation", true, 11);
  paragraph("Die folgenden Fotos und Beschreibungen dokumentieren den erfassten Zustand und die noch zu klärenden Punkte. Eine Preisberechnung ist nicht Bestandteil dieses Berichts.");
  if (feedback.trim()) {
    ensureSpace(15);
    paragraph("Rückmeldung / offene Abklärungen", true, 10);
    paragraph(feedback);
  }
  const numberedPhotos = photos.map((photo, index) => ({ photo, number: index + 1 }));
  const describedPhotos = numberedPhotos.filter(({ photo }) => photo.description.trim());
  const appendixPhotos = numberedPhotos.filter(({ photo }) => !photo.description.trim());
  let appendixIndex = 0;
  let appendixTop = 0;
  let appendixSlotHeight = 0;
  for (const { photo, number } of [...describedPhotos, ...appendixPhotos]) {
    const isAppendix = !photo.description.trim();
    if (!photo.preview) throw new Error(`Berichtsfoto „${photo.title}“ fehlt. Bitte das Foto erneut laden.`);
    const image = await context.compressImage(photo.preview);
    if (!image) throw new Error(`Berichtsfoto „${photo.title}“ konnte nicht verarbeitet werden.`);
    const dimensions = await context.imageDimensions(image);
    if (!dimensions?.width || !dimensions.height) throw new Error(`Berichtsfoto „${photo.title}“ ist nicht lesbar.`);
    if (isAppendix) {
      if (appendixIndex % 2 === 0) {
        y = context.newPage();
        if (appendixIndex === 0) paragraph("Fotoanhang – Bilder ohne Beschreibung", true, 11);
        appendixTop = y;
        appendixSlotHeight = (pageHeight - 34 - appendixTop) / 2;
      }
      y = appendixTop + (appendixIndex % 2) * appendixSlotHeight;
      // Keep a bounded caption and image slot so even portrait photos fit in pairs.
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      const captionLines = pdf.splitTextToSize(`Foto ${number}: ${photo.title}`, width) as string[];
      const caption = captionLines.slice(0, 2);
      if (captionLines.length > 2) caption[1] += "…";
      paragraph(caption.join("\n"), true, 10);
    } else {
      y = context.newPage();
      paragraph(`Foto ${number}: ${photo.title}`, true, 11);
      ensureSpace(95);
    }
    const imageMaxHeight = isAppendix
      ? appendixTop + ((appendixIndex % 2) + 1) * appendixSlotHeight - y - 8
      : 90;
    const scale = Math.min(width / dimensions.width, imageMaxHeight / dimensions.height);
    const imageWidth = dimensions.width * scale;
    const imageHeight = dimensions.height * scale;
    const format = image.startsWith("data:image/png") ? "PNG" : image.startsWith("data:image/webp") ? "WEBP" : "JPEG";
    pdf.addImage(image, format, margin + (width - imageWidth) / 2, y, imageWidth, imageHeight);
    y += imageHeight + 8;
    if (isAppendix) {
      appendixIndex++;
      continue;
    }
    if (photo.amount != null && Number.isFinite(photo.amount)) {
      paragraph(`Erfasstes Aufmaß: ${new Intl.NumberFormat("de-AT", { maximumFractionDigits: 3 }).format(photo.amount)} ${photo.unit || ""}`, false, 8);
    }
    paragraph("Schadensbeschreibung / zu klärende Punkte", true, 9);
    // Give every continuation a photo reference without truncating any description.
    const originalNewPage = context.newPage;
    context.newPage = () => {
      const top = originalNewPage();
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      pdf.text(`Foto ${number} – Beschreibung (Fortsetzung)`, margin, top);
      return top + 7;
    };
    try {
      paragraph(photo.description);
    } finally {
      context.newPage = originalNewPage;
    }
  }
}
